import type { ComponentProps, ReactNode } from 'react';

import Search from '@mui/icons-material/Search';
import { TextField, InputAdornment, Tooltip } from '@mui/material';

import type { SxProps, Theme } from '@mui/material/styles';
export type Props = {
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    dataTestId?: string;
    variant?: 'standard' | 'outlined' | 'filled';
    tooltipText?: string;
    disabled?: boolean;
    /** When true, omit the leading search icon. */
    hideSearchIcon?: boolean;
    endAdornment?: ReactNode;
    /** Merged after default `sx` so callers can override layout and styles. */
    sx?: SxProps<Theme>;
    inputProps?: ComponentProps<typeof TextField>['inputProps'];
    autoFocus?: boolean;
    fullWidth?: boolean;
};

const SearchField = ({
    placeholder = 'Search...',
    value,
    onChange,
    dataTestId,
    variant = 'standard',
    tooltipText,
    disabled,
    hideSearchIcon = false,
    endAdornment,
    sx: sxProp,
    inputProps,
    autoFocus,
    fullWidth,
}: Props) => {
    const defaultSx: SxProps<Theme> = {
        minWidth: 300,
        maxWidth: 400,
        width: '100%',
        flexShrink: 1,
        marginRight: 2,
    };

    const textField = (
        <TextField
            variant={variant}
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            fullWidth={fullWidth}
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            inputProps={inputProps}
            InputProps={{
                ...(!hideSearchIcon && {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search color={disabled ? 'disabled' : 'inherit'} />
                        </InputAdornment>
                    ),
                }),
                ...(endAdornment ? { endAdornment } : {}),
            }}
            sx={[defaultSx, ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : [])]}
            data-testid={dataTestId}
        />
    );

    return (
        <>
            {tooltipText ? (
                <Tooltip title={tooltipText} placement="bottom" arrow>
                    {textField}
                </Tooltip>
            ) : (
                textField
            )}
        </>
    );
};

export default SearchField;
